import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom'

import { MainLayout } from '@/components/layout/MainLayout'
import LoginPage from '@/pages/auth/LoginPage'
import OAuthCallbackPage from '@/pages/auth/OAuthCallbackPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import ChatPage from '@/pages/chat/ChatPage'
import ContactsPage from '@/pages/contacts/ContactsPage'
import DashboardPage from '@/pages/dashboard/DashboardPage'
import InstancesPage from '@/pages/instances/InstancesPage'
import MonitoringPage from '@/pages/monitoring/MonitoringPage'
import SettingsPage from '@/pages/settings/SettingsPage'
import UsersPage from '@/pages/users/UsersPage'
import { useAuthStore } from '@/stores/authStore'
import { canAccessPage, getDefaultRouteForRole, resolveAccessRole, type PagePermission } from '@/utils/permissions'

function GuestOnly() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const accessRole = resolveAccessRole(user)
  return isAuthenticated ? <Navigate to={getDefaultRouteForRole(accessRole)} replace /> : <Outlet />
}

function RequireAuth() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />
}

function RequirePageAccess({ page }: { page: PagePermission }) {
  const user = useAuthStore((state) => state.user)
  const accessRole = resolveAccessRole(user)
  return canAccessPage(user, page) ? <Outlet /> : <Navigate to={getDefaultRouteForRole(accessRole)} replace />
}

function RequireAIWorkspace() {
  const user = useAuthStore((state) => state.user)
  const accessRole = resolveAccessRole(user)
  return canAccessPage(user, 'monitoramento') ? <Outlet /> : <Navigate to={getDefaultRouteForRole(accessRole)} replace />
}

function LegacyAIRedirect() {
  return <Navigate to="/monitoring?tab=followup" replace />
}

function AppIndexRedirect() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const accessRole = resolveAccessRole(user)
  return <Navigate to={isAuthenticated ? getDefaultRouteForRole(accessRole) : '/login'} replace />
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass-card p-8">
        <h1>Página não encontrada</h1>
        <p>O recurso solicitado não existe na nova SPA do ISing.</p>
      </div>
    </div>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppIndexRedirect />,
  },
  {
    element: <GuestOnly />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/auth/callback', element: <OAuthCallbackPage /> },
      { path: '/register', element: <RegisterPage /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            element: <RequirePageAccess page="dashboard" />,
            children: [{ path: '/dashboard', element: <DashboardPage /> }],
          },
          { path: '/chat', element: <ChatPage /> },
          { path: '/contacts', element: <ContactsPage /> },
          {
            element: <RequirePageAccess page="monitoramento" />,
            children: [{ path: '/monitoring', element: <MonitoringPage /> }],
          },
          {
            element: <RequireAIWorkspace />,
            children: [
              { path: '/agents', element: <LegacyAIRedirect /> },
              { path: '/agents/new', element: <LegacyAIRedirect /> },
              { path: '/agents/:id/edit', element: <LegacyAIRedirect /> },
              { path: '/agent-graphs', element: <LegacyAIRedirect /> },
              { path: '/agent-graphs/new', element: <LegacyAIRedirect /> },
              { path: '/agent-graphs/:id/edit', element: <LegacyAIRedirect /> },
              { path: '/agent-graphs/:id/test', element: <LegacyAIRedirect /> },
              { path: '/skills', element: <LegacyAIRedirect /> },
              { path: '/skills/new', element: <LegacyAIRedirect /> },
              { path: '/skills/:id/edit', element: <LegacyAIRedirect /> },
              { path: '/tools', element: <LegacyAIRedirect /> },
              { path: '/rag', element: <LegacyAIRedirect /> },
              { path: '/executions', element: <LegacyAIRedirect /> },
            ],
          },
          {
            element: <RequirePageAccess page="configuracoes.geral" />,
            children: [
              { path: '/settings', element: <SettingsPage /> },
            ],
          },
          {
            element: <RequirePageAccess page="configuracoes.instancias" />,
            children: [{ path: '/instances', element: <InstancesPage /> }],
          },
          {
            element: <RequirePageAccess page="configuracoes.usuarios" />,
            children: [{ path: '/users', element: <UsersPage /> }],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
])
